import { createSelector } from "@reduxjs/toolkit";

import { getAllRecipeIngredients, getWeighingScaleValue, sortIngredients } from "../utils/experience2/ingredientsUtil";
import { IngredientsTableInterface } from "./homepage/homepage.types";
import { changeRecipeOnServing } from "./recipePage/recipePage.slice";
import { RootState } from "./store/store";
import { getDatesFromNow } from "../components/mealPlanPage/utils";

const ingredientSelector = (state: RootState) => state.homepage.ingredients;
const servingSizeSelector = (state: RootState) => state.recipePage.servingSize;
const recipeSelector = (state: RootState) => state.recipePage.recipe;
const recipeListSelector = (state: RootState) => state.homepage.recipeList;
const groceryListSelector = (state: RootState) => state.groceryList.groceryListItems;

export const ingredientsObjectSelector = createSelector(ingredientSelector, (ingredients) => {
  const ingredientsObject = ingredients.reduce((prev: IngredientsTableInterface[], next: IngredientsTableInterface) => ({
    ...prev, [next.id]: {
      ...next,
      preps: next.preps.reduce((p: any, n: any) => ({ ...p, [n.prepId]: n }), {}),
    }, // preps will eventually be an object
  }), null);
  return ingredientsObject
})

export const ingredientsPerServingSelector = createSelector(
  ingredientsObjectSelector, recipeSelector,
  (ingredientsObject, recipe) =>
    getAllRecipeIngredients(changeRecipeOnServing(recipe, 1).steps, ingredientsObject))

export const allIngredientsSelector = createSelector(
  ingredientsObjectSelector, servingSizeSelector, recipeSelector,
  (ingredientsObject, servingSize, recipe) =>
    getAllRecipeIngredients(changeRecipeOnServing(recipe, servingSize).steps, ingredientsObject))

export const filteredAllergens = createSelector(ingredientSelector, (ingredients) => {
  const uniqueAllergens: string[] = [];

  ingredients.forEach((ingredient) => {
    ingredient?.allergens.forEach((allergen: string) => {
      if (typeof allergen === 'string' && allergen.trim() !== '' && !uniqueAllergens.includes(allergen)) {
        uniqueAllergens.push(allergen);
      }
    });
  });

  return uniqueAllergens.sort();
});

export const RecipeListObject = createSelector(recipeListSelector, (recipeList) => {

  const res: Record<string, any> = {};
  if (Array.isArray(recipeList)) {
    recipeList.forEach((recipe: any) => {
      res[recipe.id] = recipe;
    })
  }
  return res;
})

export const GroceryList = createSelector(groceryListSelector, (groceryListData) => {
  if (groceryListData?.length) {
    const ingredientsAggregate: Record<string, { Ingredient: any, amount: number, unit: string }> = {};
    groceryListData.forEach((item: any) => {
      const {
        defaultWeightUnit,
        defaultUnitTriggerPoint,
        conversion,
      } = item.Ingredients_DB;
      const factor = Number.isNaN(Number(conversion[0].factor)) ? 1 : Number(conversion[0].factor);

      const roundToTwoDecimal = (value: number) => Math.round(value * 100) / 100;

      if (!ingredientsAggregate[item.ingredientId]) {
        if (defaultWeightUnit === item.unit) {
          ingredientsAggregate[item.ingredientId] = {
            Ingredient: item.Ingredients_DB,
            amount: roundToTwoDecimal(item.amount),
            unit: item.unit,
          };
        } else {
          if (defaultUnitTriggerPoint < 1) {
            ingredientsAggregate[item.ingredientId] = {
              Ingredient: item.Ingredients_DB,
              amount: roundToTwoDecimal(item.amount / (Number(factor === 0 ? 1 : factor))),
              unit: item.unit,
            };
          } else {
            ingredientsAggregate[item.ingredientId] = {
              Ingredient: item.Ingredients_DB,
              amount: roundToTwoDecimal(item.amount / (defaultUnitTriggerPoint >= 1 ? (Number(factor) * defaultUnitTriggerPoint) : 1)),
              unit: item.unit,
            };
          }
        }
      } else {
        // ingredient exists in the aggregate
        let amount = ingredientsAggregate[item.ingredientId].amount;
        if (defaultWeightUnit !== item.unit) {
          if (defaultUnitTriggerPoint < 1) {
            amount += roundToTwoDecimal(item.amount / (Number(factor === 0 ? 1 : factor)));
          } else {
            amount += roundToTwoDecimal(item.amount / ((Number(factor === 0 ? 1 : factor) * defaultUnitTriggerPoint === 0 ? 1 : defaultUnitTriggerPoint)));
          }
        } else {
          amount += roundToTwoDecimal(item.amount);
        }
        ingredientsAggregate[item.ingredientId] = {
          ...ingredientsAggregate[item.ingredientId],
          amount,
        };
      }
    });


    const ingredients: Array<{ ingredientId: string, weightScaleValue: any, ingredientMetadata: any }> = [];
    Object.entries(ingredientsAggregate).forEach(([key, {
      amount,
      unit,
      Ingredient,
    }]) => {
      const {
        defaultWeightUnit,
        defaultUnitTriggerPoint,
        conversion,
        tags
      } = Ingredient;

      const { factor } = conversion[0];

      const shouldConvert = (defaultWeightUnit === unit)
        ? amount > defaultUnitTriggerPoint && defaultUnitTriggerPoint !== 0
        : amount < Number(factor) * defaultUnitTriggerPoint;

      const conversionUnit = defaultWeightUnit === unit ? "g" : defaultWeightUnit;

      const weightScaleValue = getWeighingScaleValue(
        conversion,
        conversionUnit,
        amount,
        unit,
        shouldConvert
      );

      const isNotCommonOrProcessed = !['common', 'processed'].some(tag => tags.includes(tag));

      if (isNotCommonOrProcessed) {
        ingredients.push({
          ingredientId: key,
          weightScaleValue,
          ingredientMetadata: Ingredient
        });
      }
    });
    const sortedIngredients = sortIngredients(ingredients);
    return sortedIngredients;

  }
  return null;
})